/**
 * Semantic color tokens — the vocabulary feature code should use.
 *
 * Prefer these (`bg.surface`, `fg.muted`, `border.default`, `brand.solid`, ...)
 * over raw scale stops or hex. They give us one place to retune the palette and
 * a clean path to dark mode later (only the `_dark` values would change).
 *
 * Status colors (success/warning/error/info) are NOT defined in the Yaxle brand
 * book; they are a complementary system aligned to the palette, replacing the
 * ad-hoc status hexes scattered through the app.
 */
export const semanticTokens = {
	colors: {
		// Surfaces — no pure white; backgrounds are Cloud/Pearl only (per brand).
		// The page (canvas) is Cloud — the lightest tone. Separation comes from
		// *tonal contrast*, not borders: structural fills (table headers, panels)
		// step DOWN to Pearl (`bg.subtle`) so components read against the Cloud
		// page without outlines.
		'bg.canvas': { default: 'cloud', _dark: 'midnight' },
		'bg.surface': { default: 'cloud', _dark: 'gray.800' },
		'bg.raised': { default: 'cloud', _dark: 'gray.800' },
		'bg.subtle': { default: 'pearl', _dark: 'gray.700' },
		'bg.muted': { default: 'gray.200', _dark: 'gray.700' },
		'bg.inverse': { default: 'midnight', _dark: 'cloud' },

		// Foreground / text
		'fg.default': { default: 'graphite', _dark: 'cloud' },
		'fg.muted': { default: 'smoke', _dark: 'gray.400' },
		'fg.subtle': { default: 'gray.500', _dark: 'gray.500' },
		'fg.onBrand': { default: 'white', _dark: 'white' },
		'fg.onAccent': { default: 'midnight', _dark: 'midnight' },
		'fg.inverse': { default: 'cloud', _dark: 'graphite' },

		// Borders — definition increases down the scale. Backgrounds are Cloud/
		// Pearl, so borders must be at least a step darker to register; card and
		// section *outlines* use Smoke so content reads as distinct objects
		// rather than blending into the page.
		'border.subtle': { default: 'gray.200', _dark: 'gray.700' }, // faint dividers (table rows, list items)
		'border.default': { default: 'gray.300', _dark: 'gray.700' }, // standard, clearly visible
		'border.strong': { default: 'smoke', _dark: 'gray.500' }, // card/section outlines + emphasis

		// Brand (Apple Blue)
		'brand.solid': { default: 'brand.500' },
		'brand.hover': { default: 'brand.600' },
		'brand.active': { default: 'brand.700' },
		'brand.muted': { default: 'brand.100' },
		'brand.subtle': { default: 'brand.50' },
		'brand.fg': { default: 'brand.500', _dark: 'brand.300' },

		// Accent (Arctic Teal)
		'accent.solid': { default: 'accent.500' },
		'accent.hover': { default: 'accent.600' },
		'accent.subtle': { default: 'accent.50' },
		'accent.fg': { default: 'accent.700', _dark: 'accent.300' },

		// Status (complementary — not in the brand book)
		'success.solid': { default: 'green.500' },
		'success.subtle': { default: 'green.50' },
		'success.fg': { default: 'green.600', _dark: 'green.300' },
		'warning.solid': { default: 'orange.400' },
		'warning.subtle': { default: 'orange.50' },
		'warning.fg': { default: 'orange.600', _dark: 'orange.300' },
		'error.solid': { default: 'red.500' },
		'error.subtle': { default: 'red.50' },
		'error.fg': { default: 'red.600', _dark: 'red.300' },
		'info.solid': { default: 'brand.400' },
		'info.subtle': { default: 'brand.50' },
		'info.fg': { default: 'brand.600', _dark: 'brand.300' },
	},
} as const;

export default semanticTokens;
